import DynaRouter from 'utils/constants/abi/DynaRouter.json';
import {
  useReadContract,
  usePublicClient,
  useWriteContract,
  useSimulateContract,
} from 'wagmi';
import { QueryOptions } from 'utils/useQueryUtils';
import { ETH_AVERAGE_BLOCK_TIME } from 'utils/constants/ethConst';
import { useQuery } from '@tanstack/react-query';
import { Abi, parseAbi } from 'viem';
import {
  useEstimateContractGas,
  useQueryEstimateContractGas,
} from 'utils/hooks/useEstimateContractGas';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';

interface UsePreviewSwapParams extends QueryOptions {
  contractAddress: `0x${string}`;
  from: `0x${string}`;
  chainId?: number;
  tokenIn: `0x${string}`;
  amountIn: string;
  tokenOut: `0x${string}`;
}

export const usePreviewSwap = (params: UsePreviewSwapParams) => {
  const { from, contractAddress, chainId, tokenIn, amountIn, tokenOut } =
    params;
  const useQueryOptions = {
    queryKey: [
      'previewSwap',
      contractAddress,
      chainId,
      tokenIn,
      amountIn,
      tokenOut,
    ],
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    ...params.query,
  };

  const gas = useEstimateContractGas(
    {
      account: from,
      address: contractAddress,
      abi: DynaRouter as Abi,
      functionName: 'previewSwap',
      chainId,
      args: [tokenIn, BigInt(amountIn), tokenOut],
      query: useQueryOptions,
    },
    [contractAddress, chainId, from, tokenIn, amountIn, tokenOut]
  );

  const extraParams = {
    overrides: {
      gasLimit: gas,
    },
  };

  return useReadContract({
    account: from,
    abi: DynaRouter,
    functionName: 'previewSwap',
    chainId,
    // typescript workaround
    ...extraParams,
    address: contractAddress,
    args: [tokenIn, BigInt(amountIn), tokenOut],
    query: {
      ...useQueryOptions,
      select: (data: unknown) => {
        const d = data as [bigint, `0x${string}`, string[]];
        return {
          amountOut: d[0] as bigint,
          selectedRouter: d[1] as `0x${string}`,
          swapData: d[2] as string[],
        };
      },
    },
  });
};

interface UseSwapParams extends QueryOptions {
  contractAddress: `0x${string}`;
  chainId?: number;
  tokenIn: `0x${string}`;
  amountIn: string;
  tokenOut: `0x${string}`; // vault address
  minAmountOut: string;
  routerAddress: `0x${string}`;
  from: `0x${string}`;
  to: `0x${string}`;
  swapData: string[];
}

export const useSwap = (params: UseSwapParams) => {
  const {
    contractAddress,
    chainId,
    tokenIn,
    amountIn,
    tokenOut,
    minAmountOut,
    routerAddress,
    from,
    to,
    swapData,
  } = params;
  const useQueryOptions = {
    queryKey: [
      'swap',
      contractAddress,
      chainId,
      tokenIn,
      amountIn,
      tokenOut,
      minAmountOut,
      routerAddress,
      from,
      to,
      swapData,
    ],
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    ...params.query,
  };

  const contractCallArgs = {
    abi: DynaRouter as Abi,
    functionName: 'swap',
    chainId,
    address: contractAddress,
    account: from,
    args: [tokenIn, amountIn, tokenOut, minAmountOut, to, swapData],
    query: useQueryOptions,
  } as const;

  const gasQuery = useQueryEstimateContractGas(contractCallArgs);
  const gas = gasQuery.data || BigInt(0);

  const prepare = useSimulateContract({
    ...contractCallArgs,
    query: {
      enabled:
        contractCallArgs.query.enabled &&
        gas > BigInt(0) &&
        gasQuery.isLoading !== true &&
        gasQuery.isError !== true,
    },
    gas,
  });

  const contract = useWriteContract();

  return { prepare, contract, gasQuery };
};

interface UseSpenderAllowanceParams extends QueryOptions {
  chainId: number;
  account: `0x${string}`;
  contractAddress: `0x${string}`;
  tokenAddress: `0x${string}`;
}

export const useSpenderAllowance = (params: UseSpenderAllowanceParams) => {
  const { contractAddress, account, tokenAddress, chainId } = params;
  const useQueryOptions = {
    queryKey: queryKeys.spenderAllowance({
      contractAddress,
      ownerAddress: account,
      tokenAddress,
      chainId,
    }),
    staleTime: ETH_AVERAGE_BLOCK_TIME,
    ...params.query,
  };

  return useReadContract({
    abi: DynaRouter,
    address: contractAddress,
    functionName: 'spenderAllowance',
    account,
    chainId,
    args: [tokenAddress],
    query: useQueryOptions,
  });
};

const vaultZapperAbi = parseAbi([
  'function whitelistRegistry() view returns (address)',
  'function checkVaultAllowed(address vaultAddress) view returns (bool)',
]);
const whitelistRegistryAbi = parseAbi([
  'function isTokenAllowedForUser(address token, address user) external view returns (bool)',
]);

interface UseIsTokenAllowedForUserParams extends QueryOptions {
  account: `0x${string}`;
  chainId: number;
  userAddress: `0x${string}`;
  tokenAddress: `0x${string}`;
  contractAddress: `0x${string}`;
}

export const useIsTokenAllowedForUser = (
  params: UseIsTokenAllowedForUserParams
) => {
  const {
    account,
    chainId,
    contractAddress,
    userAddress,
    tokenAddress,
    query,
  } = params;

  const useQueryOptions = {
    queryKey: [
      'IsTokenAllowedForUser',
      account,
      chainId,
      contractAddress,
      userAddress,
      tokenAddress,
    ],
    ...query,
  };

  const client = usePublicClient({ chainId });

  return useQuery({
    queryFn: async () => {
      if (!client) {
        throw new Error('Public client not available');
      }

      const whitelistRegistryAddress = await client.readContract({
        abi: vaultZapperAbi,
        account: account,
        address: contractAddress,
        functionName: 'whitelistRegistry',
        authorizationList: undefined,
      });

      return await client.readContract({
        abi: whitelistRegistryAbi,
        args: [tokenAddress, userAddress],
        account: account,
        address: whitelistRegistryAddress,
        functionName: 'isTokenAllowedForUser',
        authorizationList: undefined,
      });
    },
    ...useQueryOptions,
  });
};

export const usePermittedVault = (params: UseIsTokenAllowedForUserParams) => {
  const {
    account,
    chainId,
    contractAddress,
    userAddress,
    tokenAddress,
    query,
  } = params;

  const useQueryOptions = {
    queryKey: [
      'IsPermittedVault',
      account,
      chainId,
      contractAddress,
      userAddress,
      tokenAddress,
    ],
    ...query,
  };

  return useReadContract({
    abi: vaultZapperAbi,
    account: account,
    chainId: chainId,
    address: contractAddress,
    functionName: 'checkVaultAllowed',
    args: [tokenAddress],
    query: useQueryOptions,
  });
};
